import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const CONSENT_KEY = "emt-consent-v1";

const applyConsent = (granted) => {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
    });
  }
};

export const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (saved) {
        applyConsent(JSON.parse(saved).marketing === true);
      } else {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
    const open = () => {
      setSettings(false);
      setVisible(true);
    };
    window.addEventListener("emt:open-consent", open);
    return () => window.removeEventListener("emt:open-consent", open);
  }, []);

  const decide = (allow) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ marketing: allow, ts: Date.now() }));
    } catch {}
    applyConsent(allow);
    setVisible(false);
    setSettings(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[60] bottom-[92px] left-4 right-4 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md"
          role="dialog"
          aria-label="Cookie-Einwilligung"
          data-testid="consent-banner"
        >
          <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_24px_60px_-20px_rgba(11,25,44,0.4)] p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-brand shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading text-base font-semibold text-ink">Ihre Privatsphäre</h2>
                <p className="mt-1.5 text-sm text-mutedtext leading-relaxed">
                  Wir nutzen Google Ads, um den Erfolg unserer Werbung zu messen. Marketing-Cookies
                  werden nur mit Ihrer Einwilligung gesetzt. Details finden Sie in der{" "}
                  <a href="/datenschutz" className="text-brand underline underline-offset-2">
                    Datenschutzerklärung
                  </a>
                  .
                </p>
              </div>
            </div>
            {settings && (
              <label
                className="mt-4 flex items-center gap-3 rounded-xl bg-surface border border-gray-200 p-3.5 cursor-pointer"
                data-testid="consent-marketing-toggle"
              >
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="w-4 h-4 shrink-0"
                  data-testid="consent-marketing-checkbox"
                />
                <span className="text-sm text-ink">
                  <span className="font-semibold block">Marketing (Google Ads)</span>
                  <span className="text-mutedtext text-xs">Conversion-Messung unserer Werbeanzeigen</span>
                </span>
              </label>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <button
                onClick={() => decide(true)}
                className="rounded-full bg-conv hover:bg-conv-dark text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-200"
                data-testid="consent-accept-all"
              >
                Alle akzeptieren
              </button>
              <button
                onClick={() => decide(false)}
                className="rounded-full border border-gray-300 text-ink text-sm font-semibold px-5 py-2.5 hover:border-brand hover:text-brand transition-colors duration-200"
                data-testid="consent-reject"
              >
                Ablehnen
              </button>
              {settings ? (
                <button
                  onClick={() => decide(marketing)}
                  className="text-sm font-semibold text-brand underline underline-offset-2"
                  data-testid="consent-save"
                >
                  Auswahl speichern
                </button>
              ) : (
                <button
                  onClick={() => setSettings(true)}
                  className="text-sm font-semibold text-brand underline underline-offset-2"
                  data-testid="consent-settings"
                >
                  Einstellungen
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
